class SlideshowIndicators extends HTMLElement {
  constructor() {
    super();
    this.slider = document.querySelector(this.dataset.targetEl);
    this.sliderList = this.slider.querySelector('ul');
    this.autoplay = (this.slider.dataset.autoplay === 'true') ? true : false;
    this.autoplayDelay = (parseInt(this.slider.dataset.autoplayDelay)) ? (parseInt(this.slider.dataset.autoplayDelay) * 1000) : 5000;
    this.isSwiping = false;
    this.startX = 0;
    this.startY = 0;

    if (!this.sliderList) {
      return;
    }

    this.slides = this.sliderList.querySelectorAll('li');
    this.observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), this.observerOptions);

    this.slides.forEach((slide, index) => {
      this.observer.observe(slide);

      slide.dataset.slideIndex = index;
    });

    this.autoplayProgress = 0;
    this.autoplayAnimationFrameId = null;
    this.currentSlideIndex = 0;
    this.userInteraction = false;

    if (this.autoplay) {
      this.startAutoplay(this.autoplayDelay);
    }

    this.slider.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.slider.addEventListener('mouseleave', () => this.resumeAutoPlay());


    this.slider.addEventListener('touchstart', (event) => {
      // Store initial touch position
      this.startX = event.touches[0].clientX;
      this.startY = event.touches[0].clientY;
      this.isSwiping = false;
    }, { passive: true });

    this.slider.addEventListener('mousedown', (event) => {
      startX = event.clientX;
      this.isSwiping = true;
      this.stopAutoPlay();
    });

    this.slider.addEventListener('touchmove', (event) => {
      if (!this.isSwiping) {
        const deltaX = Math.abs(event.touches[0].clientX - this.startX);
        const deltaY = Math.abs(event.touches[0].clientY - this.startY);

        if (deltaX > deltaY) {
          this.isSwiping = true;
          this.stopAutoPlay();
        }
      }
    }, { passive: true });

    this.slider.addEventListener('touchend', () => {
      this.isSwiping = false;
    }, { passive: true });

    this.slider.addEventListener('mouseup', () => {
      this.isSwiping = false;
    });

    this.slider.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        this.stopAutoPlay();
      }
    }, { passive: true });
  }

  handleIntersection = (entries) => {
    entries.forEach((entry, index) => {
      const slide = entry.target;
      const slideIndex = slide.dataset.slideIndex;

      if (entry.isIntersecting) {
        this.activateSlide(slideIndex);
      }
    });
  }

  activateSlide(slideIndex) {
    const activeSlide = this.querySelector('ul > li.active');
    if (activeSlide) {
      activeSlide.classList.remove('active');
      activeSlide.style.setProperty('--delay-progress', `100%`);
    }

    const targetSlide = this.querySelectorAll(`ul > li`)[slideIndex];
    if (targetSlide) {
      targetSlide.classList.add('active');
      targetSlide.style.setProperty('--delay-progress', `0%`);
    }
  }

  startAutoplay(autoplayDelay) {
    this.autoplayProgress = 0;
    let startTime = performance.now();
    let animationFrameId;

    const animateAutoplay = () => {
      const currentTime = performance.now();
      const elapsedTime = currentTime - startTime;

      if (!this.userInteraction) {
        this.logAutoplayProgress();
      }

      this.autoplayProgress = (elapsedTime / autoplayDelay) * 100;

      if (this.autoplayProgress >= 100) {
        this.currentSlideIndex = (this.currentSlideIndex + 1) % this.slides.length;
        this.changeSlide(this.currentSlideIndex);
        this.autoplayProgress = 0;
        startTime = currentTime;
      }

      if (!this.userInteraction) {
        animationFrameId = requestAnimationFrame(animateAutoplay);
      }

      this.autoplayAnimationFrameId = animationFrameId;
    };

    if (!this.autoplayAnimationFrameId) {
      animationFrameId = requestAnimationFrame(animateAutoplay);
    }

    this.autoplayAnimationFrameId = animationFrameId;
  }

  stopAutoPlay() {
    this.userInteraction = true;
    this.slider.classList.add('no-autoplay');
    this.pauseAutoplay();
  }

  resumeAutoPlay() {
    if (!this.autoplayAnimationFrameId && !this.userInteraction) {
      this.startAutoplay(this.autoplayDelay);
    }
  }

  pauseAutoplay() {
    if (this.autoplayAnimationFrameId && !this.userInteraction) {
      cancelAnimationFrame(this.autoplayAnimationFrameId);
      this.autoplayAnimationFrameId = null;
    }
  }

  logAutoplayProgress() {
    const activeIndicator = this.slider.querySelector('.active');
    if (activeIndicator) {
      const currentDelayProgress = parseFloat(activeIndicator.style.getPropertyValue('--delay-progress'));

      if (currentDelayProgress <= this.autoplayProgress && this.autoplayProgress > 5) {
        activeIndicator.style.setProperty('--delay-progress', `${this.autoplayProgress.toFixed(0)}%`);
      }
    }
  }

  changeSlide(slideIndex) {
    const targetSlide = this.slides[slideIndex];
    if (targetSlide) {
      this.sliderList.scroll({
        left: targetSlide.offsetLeft,
        behavior: 'smooth',
      });
    }
  }

  connectedCallback() {

    // add class to slider if autoplay is not enabled
    if (!this.autoplay) {
      this.slider.classList.add('no-autoplay');
    }

    const buttons = this.querySelectorAll('button');

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => {
        this.changeSlide(index);
        if (this.autoplayAnimationFrameId) {
          this.stopAutoPlay();
        }
      });
    });
  }
}

customElements.define('slideshow-indicators', SlideshowIndicators);
